import { useEffect, useState } from "react";
import { z } from "zod";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import TextField from "@mui/material/TextField";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormHelperText from "@mui/material/FormHelperText";
import Button from "@mui/material/Button";
import { IHoliday } from "../../../../../@types/holiday.type";
import vectorIcon from "../../../../../assets/_VectorIcon_.svg";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import {
  addOrUpdateHoliday,
  deleteHoliday,
  getAllHolidaysIdsAndNames,
} from "../../../../../services/api/holidays/holidaysService";
import { toast } from "react-toastify";
import { IIdName } from "../../../../../@types/key-value.type";
import { useConfirm } from "../../../../../hooks/useConfirm";
import styles from "./styles.module.scss";

const holidaySchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, "Campo obrigatório")
    .max(100, "Nome deve ter no máximo 100 caracteres"),
  holidayDate: z.string().min(1, "Campo obrigatório"),
  recurrent: z.boolean(),
  holidayType: z.object({
    id: z.string().min(1, "Selecione um tipo de feriado"),
    name: z.string().min(1),
  }),
});

export type holidayDataHookForm = z.infer<typeof holidaySchema>;

interface FormHolidayProps {
  pageType: "create" | "update";
  holidayData?: IHoliday;
}

export const FormHolidays: React.FC<FormHolidayProps> = ({
  pageType,
  holidayData,
}) => {
  const [holidayTypes, setHolidayTypes] = useState<IIdName[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<holidayDataHookForm>({
    resolver: zodResolver(holidaySchema),
    defaultValues: {
      name: holidayData?.name || "",
      recurrent: holidayData?.recurrent || false,
      holidayDate: holidayData?.holidayDate || "",
      holidayType: {
        id: holidayData?.holidayTypeId || "",
        name: holidayData?.holidayTypeName || "",
      },
    },
  });

  const [openModalConfirm, setOpenModalConfirm] = useState(false);
  const handleOpenModalConfirm = () => setOpenModalConfirm(true);
  const handleCloseModalConfirm = () => setOpenModalConfirm(false);

  const { showConfirm, Confirm } = useConfirm(
    "Tem certeza que deseja excluir o Feriado?",
    openModalConfirm,
    handleCloseModalConfirm
  );

  const navigate = useNavigate();

  useEffect(() => {
    async function fecthHolidaysIdsAndNames() {
      const response = await getAllHolidaysIdsAndNames();

      setHolidayTypes(response);
    }

    fecthHolidaysIdsAndNames();
  }, []);

  const onSubmit = async (data: holidayDataHookForm) => {
    const holidayToApi: IHoliday = {
      name: data.name,
      recurrent: data.recurrent,
      holidayDate: data.holidayDate,
      holidayTypeName: data.holidayType.name,
      holidayTypeId: data.holidayType.id,
    };

    if (holidayData) {
      holidayToApi.id = holidayData.id;
    }

    try {
      setLoading(true);
      const response = await addOrUpdateHoliday(holidayToApi);

      if (response.statusCode === 200) {
        reset();
        toast.success("Feriado salvo com sucesso!");
        navigate("/configurations/holidays");
      } else {
        toast.error(
          `Não foi possível salvar o feriado! Tente novamente mais tarde.`
        );
      }
    } catch (error) {
      console.log(error)
      toast.error(
        `Erro: Não foi possível ${pageType === "update" ? "editar" : "criar"}!`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteHoliday = async () => {
    handleOpenModalConfirm();
    const isConfirm = await showConfirm();

    if (isConfirm) {
      try {
        const response = await deleteHoliday(holidayData?.id as string);

        if (response.statusCode === 200) {
          toast.success("Feriado excluído com sucesso!");
          navigate("/configurations/holidays");
        } else {
          toast.error("erro ao excluir o Feriado! Tente novamente mais tarde.");
        }
      } catch (error) {
        console.log(error);
        toast.error("erro ao excluir o Feriado! Tente novamente mais tarde.");
      }
    }
  };

  return (
    <>
      <div className={styles.back}>
        <Button
          className={styles.button}
          onClick={() => navigate("/configurations/holidays")}
        >
          {vectorIcon && <img src={vectorIcon} />}
          Voltar a tela anterior
        </Button>
      </div>
      <div className={styles.title}>
        <h1>
          {pageType === "update" ? `${holidayData?.name}` : "Novo Feriado"}
        </h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className={styles.formHolidays}>
        <div className={styles.fields}>
          <Controller
            name="name"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                label="Feriado"
                placeholder={
                  pageType === "create"
                    ? "Insira o nome do feriado aqui..."
                    : ""
                }
                InputLabelProps={{
                  shrink: true,
                }}
                value={field.value}
                onChange={field.onChange}
                error={!!errors.name}
                helperText={errors.name?.message}
                fullWidth
                // disabled={!canEdit}
              />
            )}
          />

          <Controller
            name="recurrent"
            control={control}
            defaultValue={false}
            render={({ field }) => (
              <FormControlLabel
                style={{ flex: "none" }}
                control={
                  <Switch
                    checked={field.value || false}
                    onChange={(event) => field.onChange(event.target.checked)}
                  />
                }
                label="Recorrência (Anual)"
              />
            )}
          />
        </div>
        <div className={styles.fields}>
          <Controller
            name="holidayDate"
            control={control}
            render={({ field }) => (
              <DatePicker
                label="Data"
                value={
                  field.value
                    ? dayjs(field.value).tz("America/Sao_Paulo")
                    : null
                }
                onChange={(newValue) => {
                  field.onChange(
                    newValue ? newValue.startOf("day").toISOString() : null
                  );
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: "outlined",
                    InputLabelProps: { shrink: true },
                    error: !!errors.holidayDate,
                    helperText: errors.holidayDate?.message,
                  },
                }}
                // disabled={!canEdit}
              />
            )}
          />

          <FormControl fullWidth error={!!errors.holidayType}>
            <InputLabel shrink>Tipo do Feriado</InputLabel>
            <Controller
              name="holidayType"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  label="Tipo do Feriado"
                  displayEmpty
                  value={field.value?.id || ""}
                  onChange={(event) => {
                    const selectedId = event.target.value;
                    const selectedItem = holidayTypes.find(
                      (item) => item.id === selectedId
                    );
                    field.onChange(
                      selectedItem
                        ? { id: selectedItem.id, name: selectedItem.name }
                        : null
                    );
                  }}
                >
                  <MenuItem disabled value="">
                    Selecione...
                  </MenuItem>
                  {holidayTypes.map((item) => {
                    return (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name}
                      </MenuItem>
                    );
                  })}
                </Select>
              )}
            />
            {errors.holidayType?.id && (
              <FormHelperText>{errors.holidayType.id.message}</FormHelperText>
            )}
          </FormControl>
        </div>
        <div
          className={`${styles.btnContainer} ${
            pageType === "update" ? styles.updatePage : styles.createPage
          } `}
        >
          {pageType === "update" && (
            <Button
              variant="contained"
              color="secondary"
              type="button"
              onClick={handleDeleteHoliday}
              className={styles.btnsDeleted}
              // disabled={!canEdit}
            >
              Excluir Feriado
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            type="submit"
            // disabled={loading || !canEdit}
            className={styles.btnsSave}
          >
            Salvar
          </Button>
        </div>
      </form>
      {Confirm}
    </>
  );
};
